import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { useAuth } from "../useAuth";
import { userEmail, friendlyError } from "../lib/helpers";
import { dbLoadUserProfile } from "../lib/db";
import { PageHead } from "../components/common";

// ─────────────────────────────────────────
//  TEACHER PROFILE SCREEN
// ─────────────────────────────────────────
function TeacherProfileScreen({ onBack, lang, G, css }) {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileFields, setProfileFields] = useState({ full_name: "", email: "", new_password: "", confirm_password: "" });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  useEffect(() => {
    if (!user) return;
    setProfileFields(f => ({ ...f, full_name: user.user_metadata?.full_name || "", email: user.email || "" }));
    dbLoadUserProfile(user.id).then(r => { setRoles(r); setLoading(false); });
  }, [user]);

  const saveProfile = async () => {
    if (profileFields.new_password && profileFields.new_password !== profileFields.confirm_password) {
      setProfileMsg({ ok: false, text: "Lösenorden matchar inte." }); return;
    }
    setProfileSaving(true); setProfileMsg(null);
    try {
      const updates = {};
      if (profileFields.email !== user.email) updates.email = profileFields.email;
      if (profileFields.new_password) updates.password = profileFields.new_password;
      if (profileFields.full_name !== (user.user_metadata?.full_name || "")) updates.data = { full_name: profileFields.full_name };
      if (Object.keys(updates).length === 0) { setProfileMsg({ ok: false, text: "Inga ändringar att spara." }); setProfileSaving(false); return; }
      const { error } = await supabase.auth.updateUser(updates);
      if (error) { setProfileMsg({ ok: false, text: friendlyError(error, lang) }); }
      else {
        setProfileMsg({ ok: true, text: updates.email ? "Ändringar sparade. Bekräfta din nya e-postadress via mailet vi skickat." : "Ändringar sparade!" });
        setProfileFields(f => ({ ...f, new_password: "", confirm_password: "" }));
      }
    } catch (e) { setProfileMsg({ ok: false, text: friendlyError(e, lang) }); }
    setProfileSaving(false);
  };

  const roleLabels = {
    superadmin: "Superadmin", program_admin: "Programadmin",
    course_responsible: "Kursansvarig",
  };
  const roleColors = {
    superadmin: G.gold, program_admin: "#3b82f6",
    course_responsible: G.accent,
  };

  return (
    <div style={css.page}>

      <div style={{ maxWidth: 760, margin: "0 auto", padding: "36px 20px" }}>
        <PageHead
          G={G}
          title={lang === "sv" ? "Min profil" : "My profile"}
          sub={lang === "sv" ? "Hantera namn, e-post och lösenord." : "Manage your name, email and password."}
        />
        {/* User info */}
        <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 16, padding: "24px", marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: `${G.gold}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>
              👤
            </div>
            <div>
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 18, color: G.text, margin: 0 }}>{userEmail(user)}</p>
              <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                {roles.map((r, i) => (
                  <span key={i} style={{ display: "inline-block", background: `${roleColors[r.role] || G.muted}20`, color: roleColors[r.role] || G.muted, border: `1px solid ${roleColors[r.role] || G.muted}40`, borderRadius: 6, fontSize: 12, fontWeight: 700, padding: "2px 8px" }}>
                    {roleLabels[r.role] || r.role}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Edit profile */}
        <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 16, padding: "24px", marginBottom: 24 }}>
          <h3 style={{ ...css.heading, fontSize: 16, marginBottom: 20 }}>Redigera profil</h3>
          <label style={css.label}>Namn</label>
          <input style={{ ...css.input, marginBottom: 12 }} value={profileFields.full_name} onChange={e => setProfileFields({ ...profileFields, full_name: e.target.value })} placeholder="Förnamn Efternamn" />
          <label style={css.label}>E-postadress</label>
          <input style={{ ...css.input, marginBottom: 20 }} type="email" value={profileFields.email} onChange={e => setProfileFields({ ...profileFields, email: e.target.value })} />
          <label style={css.label}>Nytt lösenord <span style={{ color: G.muted, fontWeight: 400, textTransform: "none" }}>(lämna tomt för att behålla)</span></label>
          <input style={{ ...css.input, marginBottom: 12 }} type="password" value={profileFields.new_password} onChange={e => setProfileFields({ ...profileFields, new_password: e.target.value })} placeholder="••••••••" autoComplete="new-password" />
          <label style={css.label}>Bekräfta nytt lösenord</label>
          <input style={{ ...css.input, marginBottom: 20 }} type="password" value={profileFields.confirm_password} onChange={e => setProfileFields({ ...profileFields, confirm_password: e.target.value })} placeholder="••••••••" autoComplete="new-password" />
          {profileMsg && (
            <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: profileMsg.ok ? `${G.accent}20` : `${G.red}20`, border: `1px solid ${profileMsg.ok ? G.accent : G.red}`, color: profileMsg.ok ? G.accent : G.red, fontSize: 14 }}>
              {profileMsg.text}
            </div>
          )}
          <button style={{ ...css.btnPrimary, width: "100%" }} onClick={saveProfile} disabled={profileSaving}>{profileSaving ? "Sparar…" : "Spara ändringar"}</button>
        </div>

      </div>
    </div>
  );
}

export { TeacherProfileScreen };
